import React, { useState } from 'react';
import { DollarSignIcon, InfoIcon, BookIcon, CalendarIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { BudgetRules } from './BudgetRules';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetCardProps {
  budget: Budget;
  locale: string;
}
export function BudgetCard({
  budget,
  locale
}: BudgetCardProps) {
  // Calculate percentage of budget used
  const percentUsed = Math.min(100, Math.round((budget.totalAmount - budget.remainingAmount) / budget.totalAmount * 100));
  const percentRemaining = 100 - percentUsed;
  // Determine color based on remaining percentage
  const getProgressColor = () => {
    if (percentRemaining > 50) return 'bg-green-500';
    if (percentRemaining > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  // Format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg overflow-hidden" data-id="element-7092">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7093">
        <div className="md:col-span-2" data-id="element-7094">
          <div className="space-y-4" data-id="element-7095">
            <div data-id="element-7096">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-7097">
                {budget.name}
              </h4>
              {budget.description && <p className="mt-1 text-sm text-gray-500" data-id="element-7098">
                  {budget.description}
                </p>}
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-7099">
              <div data-id="element-7100">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-7101">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-7102" />
                  Budget Type
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-7103">
                  {budget.budgetTypeName}
                </p>
              </div>
              <div data-id="element-7104">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-7105">
                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-7106" />
                  Period
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-7107">
                  {formatDate(budget.startDate)} to {formatDate(budget.endDate)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-7108">
              <div data-id="element-7109">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-7110">
                  GL Code
                </h5>
                <p className="mt-1 text-sm font-mono text-gray-900" data-id="element-7111">
                  {budget.glCode}
                </p>
              </div>
              <div data-id="element-7112">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-7113">
                  Entity / Site
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-7114">
                  {budget.entityName || 'Global'}
                  {budget.siteName && <span className="text-gray-500" data-id="element-7115"> / {budget.siteName}</span>}
                </p>
              </div>
            </div>
            <div data-id="element-7116">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-7117">
                <DollarSignIcon className="h-3 w-3 mr-1" data-id="element-7118" />
                Budget Status
              </h5>
              <div className="mt-2 flex items-center" data-id="element-7119">
                <BudgetStatusBadge status={budget.status} data-id="element-7120" />
                <span className="ml-2 text-sm text-gray-500" data-id="element-7121">
                  Last updated:{' '}
                  {new Date(budget.updatedAt).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-1" data-id="element-7122">
          <div className="space-y-4" data-id="element-7123">
            <div data-id="element-7124">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-7125">
                Budget Amount
              </h5>
              <div className="mt-1" data-id="element-7126">
                <div className="text-2xl font-semibold text-gray-900" data-id="element-7127">
                  {formatCurrency(budget.remainingAmount, budget.currency, locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-7128">
                  remaining of{' '}
                  {formatCurrency(budget.totalAmount, budget.currency, locale)}
                </div>
              </div>
            </div>
            <div data-id="element-7129">
              <div className="flex justify-between text-sm" data-id="element-7130">
                <span data-id="element-7131">{percentRemaining}% remaining</span>
                <span data-id="element-7132">{percentUsed}% used</span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2.5" data-id="element-7133">
                <div className={`h-2.5 rounded-full ${getProgressColor()}`} style={{
                width: `${percentRemaining}%`
              }} data-id="element-7134"></div>
              </div>
            </div>
            <div className="mt-4" data-id="element-7135">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-7136">
                <InfoIcon className="h-3 w-3 mr-1" data-id="element-7137" />
                Budget Rules
              </h5>
              <div className="mt-2" data-id="element-7138">
                <BudgetRules rulesJson={budget.rules} data-id="element-7139" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}